import { ChatType, UserSelectionInput, UserType } from "@src/types.generated";

import { ChatDetailFragment } from "./ChatDetailPage.generated";

export const isUserOnlySelection = (selection: UserSelectionInput): boolean =>
  Boolean(selection.userIds?.length) &&
  !selection.allLocations &&
  !selection.allManagers &&
  !selection.allTrainees &&
  !selection.allAdmins &&
  !selection.locationIds?.length &&
  !selection.locationGroupIds?.length &&
  !selection.roleIds?.length &&
  !selection.roleGroupIds?.length &&
  selection.daysAfterHire == null;

export const isSingleUserSelection = (
  selection: UserSelectionInput,
  viewerId: number | undefined,
): boolean =>
  isUserOnlySelection(selection) &&
  (selection.userIds ?? []).filter((id) => id !== viewerId).length <= 1;

type ChatDisplayMember = {
  id: number;
};

type ChatDisplayData = {
  chatType: ChatType;
};

export const getChatOtherMembers = <M extends ChatDisplayMember>(
  members: M[],
  viewerId: number | undefined,
): M[] => {
  const others = members.filter((member) => member.id !== viewerId);
  return others.length > 0 ? others : members;
};

export const isDirectMessage = (chat: ChatDisplayData): boolean =>
  chat.chatType === ChatType.OneOnOne ||
  chat.chatType === ChatType.DirectMessage;

export const getChatTypeLabel = (directMessage: boolean): string =>
  directMessage ? "Direct Message" : "Group Message";

export const isGroupMessage = (chat: ChatDisplayData): boolean =>
  chat.chatType === ChatType.Group || chat.chatType === ChatType.GroupMessage;

export const UNTITLED_GROUP_MESSAGE = "Untitled Group Message";

export const getChatDisplayTitle = (
  chat: ChatDisplayData & { displayTitle: string },
): string =>
  chat.displayTitle || (isGroupMessage(chat) ? UNTITLED_GROUP_MESSAGE : "");

const USER_TYPE_LABELS: Record<UserType, string> = {
  [UserType.Admin]: "Admin",
  [UserType.Manager]: "Manager",
  [UserType.Employee]: "Employee",
};

export const getMemberRoleSubtitle = (member: {
  userType: UserType;
  roles: { name: string }[];
  locations: { name: string }[];
}): string => {
  const role = member.roles[0]?.name || USER_TYPE_LABELS[member.userType];
  const location = member.locations[0]?.name;
  return location ? `${role} at ${location}` : role;
};

export type ChatDisplay = {
  title: string;
  subtitle: string | null;
  description: string | null;
  avatarPerson: ChatDetailFragment["members"][number] | null;
  composerName: string;
};

const oneOnOneSubtitle = (
  member: ChatDetailFragment["members"][number],
): string => getMemberRoleSubtitle(member);

export const getChatDisplay = (
  chat: ChatDetailFragment | null | undefined,
  viewerId: number | undefined,
): ChatDisplay => {
  if (!chat) {
    return {
      title: "",
      subtitle: null,
      description: null,
      avatarPerson: null,
      composerName: "",
    };
  }
  if (isDirectMessage(chat)) {
    const other = getChatOtherMembers(chat.members, viewerId)[0];
    return {
      title: getChatDisplayTitle(chat),
      subtitle: oneOnOneSubtitle(other),
      description: null,
      avatarPerson: other,
      composerName: other.firstName,
    };
  }
  return {
    title: getChatDisplayTitle(chat),
    subtitle: `Created by ${chat.createdBy.name}`,
    description: chat.description?.en?.trim() || null,
    avatarPerson: null,
    composerName: getChatDisplayTitle(chat),
  };
};
